import axios from "axios";
import { authState, keycloak, getAuthBootstrap } from "@/plugins/keycloak";
import { configService } from "@/infrastructure/services/ConfigService";
import { logger } from "@/infrastructure/services/LoggerService";
import { updateActivity, checkSessionValidity, clearSession } from "@/shared/utils/sessionManager";
/**
 * Path fragment for the auth bootstrap endpoint. The request gate skips this
 * URL because the bootstrap call itself is what resolves the
 * `authBootstrap.awaitInitial()` promise — gating it would deadlock.
 */
const BOOTSTRAP_PATH = "/auth/bootstrap";
/** Create an Axios instance with default settings */
const api = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
});
/** Request Interceptor to include the Keycloak token and set base URL */
api.interceptors.request.use(async (config) => {
    // Gate every business call on the auth-bootstrap initial promise so that
    // the user's default roles are materialized in Keycloak before any
    // downstream API hits a 401/403. Skip the bootstrap call itself —
    // gating it would deadlock against its own resolution.
    const isBootstrap = config.url?.includes(BOOTSTRAP_PATH) ?? false;
    if (!isBootstrap) {
        await getAuthBootstrap().awaitInitial();
    }
    if (!config.baseURL) {
        try {
            // Get the base URL from ConfigService based on apiType
            const apiConfig = configService.api;
            const apiType = config.apiType || "opay"; // Default to opay API
            if (apiType === "bff" && apiConfig?.bffUrl) {
                config.baseURL = apiConfig.bffUrl;
            }
            else if (apiConfig?.baseUrl) {
                config.baseURL = apiConfig.baseUrl;
            }
            else {
                logger.debug(`API base URL (${apiType}) not available from ConfigService`);
                throw new Error(`Base URL (${apiType}) not set`);
            }
        }
        catch (error) {
            logger.debug("Failed to get API base URL from ConfigService:", error);
            throw new Error("Base URL not set");
        }
    }
    if (authState.isAuthenticated && keycloak.token) {
        // Since api.ts needs the latest Keycloak token, but it's outside Vue's reactivity system, always get the latest token dynamically
        config.headers.Authorization = `Bearer ${keycloak.token}`;
        // Update activity timestamp on authenticated API calls
        updateActivity();
    }
    return config;
}, (error) => Promise.reject(error));
/** Response Interceptor to handle token refresh on 401/403 */
api.interceptors.response.use((response) => response, async (error) => {
    const originalRequest = error.config;
    // Auth-bootstrap recovery branch: when a business call fails with 401/403
    // and the bootstrap state is anything other than "succeeded", force a
    // full token refresh via authBootstrap.recover() then retry the original
    // request once. The per-request `_recoveryAttempted` flag prevents loops.
    // Runs BEFORE the existing updateToken(30) path so we subsume the
    // lightweight refresh when the bootstrap hasn't yet completed.
    const status = error.response?.status;
    if (status === 401 || status === 403) {
        const svc = getAuthBootstrap();
        if (originalRequest &&
            !originalRequest._recoveryAttempted &&
            svc.getState() !== "succeeded") {
            originalRequest._recoveryAttempted = true;
            await svc.recover();
            return api.request(originalRequest);
        }
    }
    // Handle 401 (Unauthorized) and 403 (Forbidden) responses
    if ((error.response?.status === 401 || error.response?.status === 403) &&
        !originalRequest._retryAttempted) {
        // Mark this request as having been retried to prevent infinite loops
        originalRequest._retryAttempted = true;
        // Check session validity first (8h inactivity, 16h max lifetime)
        const sessionState = checkSessionValidity();
        if (!sessionState.isValid) {
            logger.debug("Session expired on API call, redirecting to session-ended", {
                status: error.response?.status,
                reason: sessionState.reason,
            });
            authState.isAuthenticated = false;
            clearSession();
            // Redirect to session ended page
            if (window.location.pathname !== "/session-ended" &&
                !window.location.pathname.startsWith("/payment")) {
                window.location.href = "/session-ended";
            }
            return Promise.reject(error);
        }
        try {
            // Attempt to refresh the token
            await keycloak.updateToken(30);
            logger.debug("Token refreshed successfully after " + error.response?.status);
            // Retry the failed request with the new token
            originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
            return api.request(originalRequest);
        }
        catch (refreshError) {
            logger.debug("Token refresh failed on API " +
                error.response?.status +
                ", redirecting to session-ended", refreshError);
            authState.isAuthenticated = false;
            clearSession();
            // Redirect to session ended page instead of login
            if (window.location.pathname !== "/session-ended" &&
                !window.location.pathname.startsWith("/payment")) {
                window.location.href = "/session-ended";
            }
            return Promise.reject(refreshError);
        }
    }
    return Promise.reject(error);
});
export default api;
